/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import noop from 'lodash/noop';
import { useParams } from 'react-router-dom';
import { Col, Row } from 'antd';

import { useVideosByIds, useTag, usePlaylist } from '@api/hooks';
import api from '@api';
import {
  ContentModuleModal,
  ArticleTagField,
  AddSelectEntity,
  PlaylistField,
  VideoSelect,
  ModeSelect,
  TextField,
} from '@molecules';
import { NO_MARGIN_BOTTOM } from '@constants';
import { Typography } from '@components/atoms';

import Form from '../Form';

import { FORM_FIELD, FORM_FIELDS, INITIAL_VALUES, MODE, MODES } from './constants';
import LayoutField from './LayoutField';
import VideoList from './VideoList';
import Item from './Item';

const VideoLibraryModal = ({
  visible,
  hide,
  previewImg,
  onDelete,
  onSubmit,
  initialValues: data = INITIAL_VALUES,
  errors,
}) => {
  const [form] = Form.useForm(FORM_FIELDS);
  const { locale } = useParams();

  const videosQuery = useVideosByIds(
    data.videoIds?.map((id) => (id.split('_').length > 1 ? id : `${id}_${locale || 'en'}`)),
    locale,
    { passErrors: true },
  );

  React.useEffect(() => {
    if (visible && videosQuery.status === 'success') {
      form.setFieldsValue({ [FORM_FIELD.VIDEOS.name]: videosQuery.videos });
    }
  }, [videosQuery.videos, videosQuery.status, form, visible]);

  const tagQuery = useTag(data.tagId);
  React.useEffect(() => {
    if (visible && tagQuery.status === 'success') {
      form.setFieldsValue({ [FORM_FIELD.TAG.name]: tagQuery.data });
    }
  }, [form, tagQuery.data, tagQuery.status, tagQuery.tag, visible]);

  const playlistQuery = usePlaylist({ id: data?.videoPlaylistId });
  React.useEffect(() => {
    if (visible && playlistQuery.status === 'success') {
      form.setFieldsValue({ [FORM_FIELD.PLAYLIST.name]: playlistQuery.data });
    }
  }, [form, playlistQuery, visible]);

  const [mode, setMode] = React.useState();
  React.useEffect(() => {
    if (visible) {
      if (data.tagId) setMode(MODE.TAG.value);
      else if (data.videoPlaylistId) setMode(MODE.PLAYLIST.value);
      else setMode(MODE.MANUAL.value);
    }
  }, [data.tagId, data.videoPlaylistId, visible]);

  React.useEffect(() => {
    if (mode === MODE.MANUAL.value) form.setFieldsValue({ [FORM_FIELD.SLUG.name]: null });
  }, [mode, form]);

  const initialValues = React.useMemo(() => form.deserialize(data), [data, form]);

  const handleClose = () => {
    form.resetFields();
    hide();
  };

  const handleSubmit = (values) => {
    onSubmit(values);
    handleClose();
  };

  const handleAddVideo = async (video) => {
    // can be undefined if only tag was saved to API
    const videos = form.getFieldValue(FORM_FIELD.VIDEOS.name) || [];
    form.setFieldsValue({ [FORM_FIELD.VIDEOS.name]: [...videos, video] });
  };

  return (
    <ContentModuleModal
      okText={onDelete ? 'SAVE CHANGES' : 'ADD VIDEO MODULE'}
      title="Inside Track Video Module"
      subtitle="Add video module."
      heroImageSrc={previewImg}
      onCancel={handleClose}
      onSubmit={form.submit}
      onDelete={onDelete}
      visible={visible}
      errors={errors}
    >
      <Form
        onSerializedFinish={handleSubmit}
        initialValues={initialValues}
        name="insideTrackVideo"
        layout="vertical"
        form={form}
      >
        <TextField {...FORM_FIELD.TITLE} wrapperCol={{ span: 11 }} />
        <Typography.Title level={3}>Add a video</Typography.Title>
        <Row style={{ paddingBottom: mode !== MODE.MANUAL.value ? 50 : 0 }}>
          <Col span={3}>
            <Typography.Title level={4}>Add</Typography.Title>
          </Col>
          <Col span={14} flex={1}>
            <ModeSelect mode={mode} modes={MODES} onSelect={setMode} wrapperCol={{ span: 15 }} />
            {mode === MODE.MANUAL.value && (
              <>
                <AddSelectEntity onAdd={handleAddVideo} select={VideoSelect} addText="ADD VIDEO" isTypeFilter />
              </>
            )}
            {mode === MODE.TAG.value && <ArticleTagField {...FORM_FIELD.TAG} style={NO_MARGIN_BOTTOM} />}
            {mode === MODE.PLAYLIST.value && <PlaylistField {...FORM_FIELD.PLAYLIST} style={NO_MARGIN_BOTTOM} />}
          </Col>
        </Row>
        <Row>
          <Col span={21}>
            <Typography.Title level={3}>Layout</Typography.Title>
            <LayoutField {...FORM_FIELD.LAYOUT} />
          </Col>
        </Row>
        {mode === MODE.MANUAL.value && <VideoList form={form} style={NO_MARGIN_BOTTOM} />}

        <Form.WithFieldValue name="tag">
          {(_value, _form) => {
            if (_value) {
              const { relatedId, type } = _value;

              fetchRelatedTagContent({ type, id: relatedId, _form });
            }
          }}
        </Form.WithFieldValue>
        <Form.WithFieldValue name={FORM_FIELD.PLAYLIST.name}>
          {(_value, _form) => {
            if (_value) {
              const { slug } = _value;

              _form.setFieldsValue({ [FORM_FIELD.SLUG.name]: slug });
            }
          }}
        </Form.WithFieldValue>
        {(mode === MODE.TAG.value || mode === MODE.PLAYLIST.value) && (
          <Form.Item noStyle name={FORM_FIELD.SLUG.name}>
            {noop}
          </Form.Item>
        )}
      </Form>
    </ContentModuleModal>
  );

  async function fetchRelatedTagContent({ type, id, _form }) {
    let result;

    if (type === 'discipline') {
      const req = await api.disciplines.getByIds({ ids: [id] });
      result = req?.length && req[0];
    }

    if (type === 'event') result = await api.events.getById({ id });

    if (type === 'competitor') {
      result = await api.athletes.getById({ id });
    }

    if (type === 'competition') result = await api.competitions.getById({ id });

    if (type === 'static') result = { slug: id };

    _form.setFieldsValue({ [FORM_FIELD.SLUG.name]: result?.slug || result?.urlSlug });
  }
};

VideoLibraryModal.Item = Item;

export default VideoLibraryModal;
